import type { Store } from '@tanstack/react-store';
import type { SortingState } from '@tanstack/react-table';

type SortingStateInput =
  | SortingState
  | ((oldSorting: SortingState) => SortingState);

interface BaseManagementState {
  filters: Record<string, unknown>;
  sorting: SortingState;
  pagination: {
    currentPage: number;
    totalPages: number;
    total: number;
  };
  ui: {
    isLoading: boolean;
  };
}

interface ManagementActionsConfig<TFilters> {
  initialFilters: TFilters;
  defaultSorting: SortingState;
}

export function createManagementActions<TState extends BaseManagementState>(
  store: Store<TState>,
  config: ManagementActionsConfig<TState['filters']>,
) {
  type FilterKey = keyof TState['filters'];
  type FilterValue = TState['filters'][FilterKey];

  const updateFilter = (filterType: FilterKey, value: FilterValue) => {
    store.setState(
      (prev) =>
        ({
          ...prev,
          filters: { ...prev.filters, [filterType as string]: value },
          pagination: { ...prev.pagination, currentPage: 1 },
        }) as TState,
    );
  };

  const setSorting = (sorting: SortingStateInput) => {
    store.setState(
      (prev) =>
        ({
          ...prev,
          sorting:
            typeof sorting === 'function' ? sorting(prev.sorting) : sorting,
          pagination: { ...prev.pagination, currentPage: 1 },
        }) as TState,
    );
  };

  const setPage = (page: number) => {
    store.setState(
      (prev) =>
        ({
          ...prev,
          pagination: { ...prev.pagination, currentPage: page },
        }) as TState,
    );
  };

  const setPaginationInfo = (
    currentPage: number,
    totalPages: number,
    total: number,
  ) => {
    store.setState(
      (prev) =>
        ({
          ...prev,
          pagination: { currentPage, totalPages, total },
        }) as TState,
    );
  };

  const setLoading = (isLoading: boolean) => {
    store.setState(
      (prev) =>
        ({
          ...prev,
          ui: { ...prev.ui, isLoading },
        }) as TState,
    );
  };

  const resetFilters = () => {
    store.setState(
      (prev) =>
        ({
          ...prev,
          filters: config.initialFilters,
          sorting: config.defaultSorting,
          pagination: { ...prev.pagination, currentPage: 1 },
        }) as TState,
    );
  };

  /**
   * Applies a parsed URL query (filters, sort, page) in one update and skips
   * the write when nothing changed, so URL sync never loops.
   */
  const setQueryState = ({
    filters,
    sorting,
    page,
  }: {
    filters?: Partial<TState['filters']>;
    sorting?: SortingState;
    page?: number;
  }) => {
    store.setState((prev) => {
      const nextFilters = filters
        ? { ...prev.filters, ...filters }
        : prev.filters;
      const nextSorting = sorting ?? prev.sorting;
      const nextPage = page ?? prev.pagination.currentPage;

      if (
        JSON.stringify([nextFilters, nextSorting, nextPage]) ===
        JSON.stringify([
          prev.filters,
          prev.sorting,
          prev.pagination.currentPage,
        ])
      ) {
        return prev;
      }

      return {
        ...prev,
        filters: nextFilters,
        sorting: nextSorting,
        pagination: { ...prev.pagination, currentPage: nextPage },
      } as TState;
    });
  };

  const setUiField = <K extends keyof TState['ui']>(
    field: K,
    value: TState['ui'][K],
  ) => {
    store.setState(
      (prev) =>
        ({
          ...prev,
          ui: { ...prev.ui, [field as string]: value },
        }) as TState,
    );
  };

  return {
    updateFilter,
    setSorting,
    setPage,
    setPaginationInfo,
    setLoading,
    resetFilters,
    setQueryState,
    setUiField,
  };
}
